import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { Observable, of, from, defer, EMPTY, merge } from "rxjs"
import { switchMap, map, catchError, filter, delay, delayWhen, mergeMap } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import {
    FETCH_ORDER_DETAIL_REQUESTED,
    FETCH_ORDERS_REPORT_REQUESTED,
    fetchOrderDetailFailure,
    fetchOrderDetailRequested,
    fetchOrderDetailSuccess,
    fetchOrdersReportFailure,
    fetchOrdersReportRequested,
    fetchOrdersReportSuccess,
} from "../../../../api/actions"
import { getHttpHeaders, getSelectedAccountId } from "../../../auth/selectors"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import {
    clearOrdersData,
    CLOSE_ORDER_FORM_CLICKED,
    EDIT_ORDER_OPENED,
    GET_PAYMENT_REQUEST_DETAIL_REQUESTED,
    getPaymentRequestDetailSuccess,
    ORDERS_AGENDA_MOUNTED,
    ordersAgendaMounted,
    SEND_ORDER_EMAIL_CLICKED,
    SEND_ORDER_EMAIL_FAILURE,
    SEND_ORDER_EMAIL_SUCCESS,
    sendOrderEmailFailure,
    sendOrderEmailResetDefault,
    sendOrderEmailSuccess,
} from "./actions"
import {
    createLoadingEnded,
    createLoadingStarted,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_ORDERS_REPORT_DEFAULT_FILTER,
    SET_PAGINATION,
    setFilterValue,
    setOrdersReportDefaultFilter,
    setPagination,
    setTotalCount,
} from "../../../filters/actions"
import type { Action, StateObject } from "../../../redux/types"
import {
    getOrdersByPageNumber,
    getOrdersCurrentPage,
    getOrdersQueryApiParam,
    isOrderReportAgendaOpened,
} from "./selectors"
import { Router } from "../../../../server/router"
import { waitForAccountSelected, waitForGlobalFilterSet } from "../customers/customersTable/epics"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { getCurrentPath } from "../../../router/selectors"
import { areAnyLocationsSelected, getFilters } from "../../../filters/selectors"
import { fetchOrderById, fetchOrdersReportData, fetchPaymentRequestDetail, orderReceiptSend } from "./request"
import type { OrderDetail, ReceiptSendRequestData } from "../../../../graphql/types"
import { createActionLoadingError } from "../../../data/actions"
import { OrderSourceEnum } from "./types"
import { getMidnightOffset } from "../../settings/selectors"
import { ISO_DATE_FORMAT } from "../../../uiKit/DateRangePicker/utils"
import { format } from "date-fns"
import { sanitizeSearchText, sanitizeUrlDateRange } from "../queryParamsSanitizer"

export const orderAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ORDERS_AGENDA_MOUNTED),
        mergeMap(() => {
            // If the url have query filters, we want to store them in redux
            const queryFilters = Router.query
            if ((queryFilters.from && queryFilters.to) || queryFilters.searchText) {
                const setEachFilterActions: Action[] = []
                const midnightOffset = getMidnightOffset(deps.getState())
                const { from, to, searchText } = queryFilters

                // date range filter
                const safeDateRange = sanitizeUrlDateRange(
                    { from: from, to: to },
                    { midnightOffsetMinutes: midnightOffset ?? 0 }
                )
                if (safeDateRange) {
                    setEachFilterActions.push(
                        setFilterValue("global", "date", { from: safeDateRange.dateFrom, to: safeDateRange.dateTo })
                    )
                    setEachFilterActions.push(
                        setFilterValue("global", "timestamp", {
                            from: safeDateRange.timestampFrom,
                            to: safeDateRange.timestampTo,
                        })
                    )
                }

                // search text filter
                const safeSearch = sanitizeSearchText(searchText, { maxLength: 200 })
                if (safeSearch !== undefined) {
                    setEachFilterActions.push(setFilterValue("orders", "search", { value: safeSearch }))
                }

                return [setOrdersReportDefaultFilter(), ...setEachFilterActions]
            }

            // Otherwise we want to setup url query params based on the global filters or default filters
            const globalDateFilters = getFilters(deps.getState(), GLOBAL_FILTER_IDENTIFIER)?.date
            const from = globalDateFilters?.from ?? format(new Date(), ISO_DATE_FORMAT)
            const to = globalDateFilters?.to ?? format(new Date(), ISO_DATE_FORMAT)
            Router.push({
                pathname: Router.asPath.split("?")[0],
                query: { ...Router.query, from: from, to: to },
            })
            return [setOrdersReportDefaultFilter()]
        })
    )

export const fetchOrdersReportOnAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isOrderReportAgendaOpened(deps.getState())),
        map(() => ordersAgendaMounted())
    )

export const nextOrderPageRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_PAGINATION),
        delayWhen((action: Action) => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        filter(action => action.payload.identifier && action.payload.identifier === "orders"),
        switchMap(action => {
            const pageToGo = action.payload.paginationValue.currentPage

            if (!pageToGo) {
                return []
            }

            const pageData = getOrdersByPageNumber(deps.getState(), pageToGo)
            return !pageData || pageData.length === 0 ? [fetchOrdersReportRequested()] : []
        })
    )

export const ordersReportFilterChanged = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(SET_ORDERS_REPORT_DEFAULT_FILTER)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "orders" || action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        )
    ).pipe(
        filter(() => isOrderReportAgendaOpened(deps.getState())),
        switchMap(() => {
            return [
                clearOrdersData(),
                setPagination("orders", {
                    currentPage: 1,
                }),
            ]
        })
    )

export const cashReportLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_ORDERS_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isOrderReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("orders")])
    )

export const fetchOrdersReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_ORDERS_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isOrderReportAgendaOpened(deps.getState())),
        delayWhen(action => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                const params = getOrdersQueryApiParam(deps.getState())
                return defer(() =>
                    fetchOrdersReportData(getHttpHeaders(deps.getState()), {
                        from: params.from,
                        to: params.to,
                        count: params.count,
                        offset: params.offset,
                        timezone: params.timezone,
                        locationIds: params.location_ids,
                        search: params.search,
                        source: params.source,
                        sort: params.sort,
                        sortOrder: params.sort_order,
                        isActive: params.is_active,
                        assigneeIds: params.assignee_ids,
                    })
                ).pipe(
                    switchMap(result => {
                        if (result && result.data) {
                            return [
                                createLoadingEnded("orders"),
                                setTotalCount("orders", result.totalCount),
                                fetchOrdersReportSuccess(result.data as any, getOrdersCurrentPage(deps.getState())),
                            ]
                        }

                        return [fetchOrdersReportFailure(), createLoadingEnded("orders")]
                    }),
                    catchError(error => {
                        return of(createActionLoadingError(error))
                    })
                )
            }

            return [createLoadingEnded("orders")]
        })
    )

export const fetchOrderDetaiOnDetailOpenedlEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(EDIT_ORDER_OPENED),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        filter(action => action.payload.orderId),
        switchMap(action => [createLoadingStarted("order-detail"), fetchOrderDetailRequested(action.payload.orderId)])
    )

export const fetchOrderDetailRequestedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_ORDER_DETAIL_REQUESTED),
        switchMap((action: Action) => {
            return defer(() => fetchOrderById(getHttpHeaders(deps.getState()), action.payload.orderId)).pipe(
                switchMap((response: Maybe<OrderDetail>) => {
                    if (response) {
                        return [createLoadingEnded("order-detail"), fetchOrderDetailSuccess(response)]
                    } else {
                        return [
                            createLoadingEnded("order-detail"),
                            fetchOrderDetailFailure({
                                status: 404,
                            }),
                        ]
                    }
                }),
                catchError(error => from([fetchOrderDetailFailure(error), createLoadingEnded("order-detail")]))
            )
        })
    )

export const orderClosedRouterEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CLOSE_ORDER_FORM_CLICKED),
        switchMap(() => {
            const urlQueries = getCurrentPath(deps.getState()).query

            if (urlQueries.agenda === "customers" && urlQueries.id && urlQueries.tab !== "feedback") {
                Router.push(`/reports/customers/edit/${urlQueries.id}`)
            } else if (urlQueries.agenda === "customers" && urlQueries.tab === "feedback") {
                Router.push("/reports/customers/feedback")
            } else {
                const { id: _id, ...otherQueryParams } = Router.query
                Router.push({
                    pathname: "/reports/orders/orders",
                    query: otherQueryParams,
                })
            }

            return EMPTY
        })
    )

export const orderSendEmailEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SEND_ORDER_EMAIL_CLICKED),
        switchMap((action: Action) => {
            const accountId = getSelectedAccountId(deps.getState())

            if (accountId) {
                const template =
                    action.payload.orderSource && action.payload.orderSource === OrderSourceEnum.MiB
                        ? "mib_order_customer"
                        : "pos_order"
                const data: ReceiptSendRequestData = {
                    orderId: action.payload.orderId,
                    transactionId: action.payload.transactionId,
                    accountId: accountId,
                    emailAddress: action.payload.email,
                    template: template,
                    orderSource: action.payload.orderSource,
                }
                const headers = { ...getHttpHeaders(deps.getState()), locationId: action.payload.locationId }
                return defer(() => orderReceiptSend(headers, data)).pipe(
                    switchMap(res => {
                        if (res && res.success) {
                            return [sendOrderEmailSuccess()]
                        }

                        return [sendOrderEmailFailure()]
                    }),
                    catchError(error => of(createActionLoadingError(error)))
                )
            }

            return []
        })
    )

export const resetEmailStateEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SEND_ORDER_EMAIL_SUCCESS, SEND_ORDER_EMAIL_FAILURE),
        delay(2000),
        map(() => sendOrderEmailResetDefault())
    )

export const fetchPaymentRequestDetailRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(GET_PAYMENT_REQUEST_DETAIL_REQUESTED),
        filter((action: Action) => action.payload.id),
        switchMap(action => {
            return defer(() => fetchPaymentRequestDetail(getHttpHeaders(deps.getState()), action.payload.id)).pipe(
                switchMap(result => {
                    if (result) {
                        return [
                            getPaymentRequestDetailSuccess({
                                [action.payload.id]: result,
                            }),
                            createLoadingEnded("payment-info"),
                        ]
                    }

                    return []
                }),
                catchError(error => {
                    return from([createLoadingEnded("payment-info"), createActionLoadingError(error)])
                })
            )
        })
    )

export const fetchPaymentRequestDetailRequestedLoadingEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(GET_PAYMENT_REQUEST_DETAIL_REQUESTED),
        filter((action: Action) => action.payload.id),
        switchMap(() => {
            return [createLoadingStarted("payment-info")]
        })
    )

export default [
    resetEmailStateEpic,
    orderSendEmailEpics,
    cashReportLoadingStartedEpics,
    orderAgendaMountedEpics,
    nextOrderPageRequestedEpics,
    fetchOrdersReportRequestedEpics,
    ordersReportFilterChanged,
    fetchOrdersReportOnAccountSwitch,
    orderClosedRouterEpics,
    fetchOrderDetaiOnDetailOpenedlEpics,
    fetchOrderDetailRequestedEpic,
    fetchPaymentRequestDetailRequestedEpics,
    fetchPaymentRequestDetailRequestedLoadingEpics,
]
